import React from 'react';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Info, TrendingUp, TrendingDown, Minus, Zap, BarChart3, AlertCircle } from 'lucide-react';

export default function DocsCIScore() {
  return (
    <div className="max-w-4xl">
      <div className="mb-8">
        <Badge className="bg-[#3EF1C7]/20 text-[#3EF1C7] border-[#3EF1C7]/30 mb-4">
          Concepts
        </Badge>
        <h1 className="text-3xl font-bold text-white mb-4" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
          CI Score v0 – Early Commerce Index Signal
        </h1>
        <p className="text-[#94A3B8] text-lg">
          CI Score v0 is a 0–100 score that gives you a simple, early signal of how a brand or product is performing based on real activity, not guesses.
        </p>
      </div>

      {/* What is CI Score */}
      <section className="mb-12">
        <h2 className="text-xl font-semibold text-white mb-4 flex items-center gap-2">
          <Info className="w-5 h-5 text-[#3EF1C7]" />
          What is CI Score v0?
        </h2>
        <div className="prose prose-invert max-w-none">
          <p className="text-[#94A3B8] mb-4">
            CI Score v0 is a 0–100 score that summarizes how healthy a brand or product is right now, based on:
          </p>
          <ul className="text-[#94A3B8] space-y-2 mb-6 list-disc list-inside">
            <li>How much activity it is getting (views + purchases)</li>
            <li>How efficiently that activity converts (conversion rate)</li>
            <li>How those compare to its own recent history and peers (within the brand)</li>
          </ul>
          
          <div className="grid md:grid-cols-2 gap-4 mb-6">
            <Card className="bg-[#0D1628] border-[#22304A]">
              <CardHeader className="pb-2">
                <CardTitle className="text-white text-lg flex items-center gap-2">
                  <BarChart3 className="w-5 h-5 text-[#7A5CFF]" />
                  Product CI Score
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-[#94A3B8] text-sm">
                  How this SKU is doing vs the rest of the brand&apos;s catalog.
                </p>
              </CardContent>
            </Card>
            <Card className="bg-[#0D1628] border-[#22304A]">
              <CardHeader className="pb-2">
                <CardTitle className="text-white text-lg flex items-center gap-2">
                  <TrendingUp className="w-5 h-5 text-[#3EF1C7]" />
                  Brand CI Score
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-[#94A3B8] text-sm">
                  How the brand is doing vs its own recent past.
                </p>
              </CardContent>
            </Card>
          </div>
          
          <p className="text-[#94A3B8]">
            The score is <strong className="text-white">relative</strong> and <strong className="text-white">event-driven</strong>:
          </p>
          <ul className="text-[#94A3B8] space-y-2 mb-4 list-disc list-inside">
            <li><strong className="text-white">Relative:</strong> Uses your own catalog and history as a baseline, not arbitrary external thresholds.</li>
            <li><strong className="text-white">Event-driven:</strong> Computed directly from the product-view and product-purchase events you send via CI Proof or the /collect APIs.</li>
          </ul>
        </div>
      </section>

      {/* Tiers */}
      <section className="mb-12">
        <h2 className="text-xl font-semibold text-white mb-4 flex items-center gap-2">
          <Zap className="w-5 h-5 text-[#FFD166]" />
          CI Score Tiers
        </h2>
        
        <div className="mb-6">
          <h3 className="text-lg font-medium text-white mb-3">Product CI Tiers</h3>
          <div className="grid grid-cols-3 gap-4">
            <div className="bg-[#3EF1C7]/10 border border-[#3EF1C7]/30 rounded-lg p-4 text-center">
              <div className="text-2xl font-bold text-[#3EF1C7]">75–100</div>
              <div className="text-white font-medium">Hot</div>
              <p className="text-[#94A3B8] text-sm mt-1">Strong performer</p>
            </div>
            <div className="bg-[#94A3B8]/10 border border-[#94A3B8]/30 rounded-lg p-4 text-center">
              <div className="text-2xl font-bold text-[#94A3B8]">40–74</div>
              <div className="text-white font-medium">Steady</div>
              <p className="text-[#94A3B8] text-sm mt-1">Normal activity</p>
            </div>
            <div className="bg-[#FF6B6B]/10 border border-[#FF6B6B]/30 rounded-lg p-4 text-center">
              <div className="text-2xl font-bold text-[#FF6B6B]">0–39</div>
              <div className="text-white font-medium">Cold</div>
              <p className="text-[#94A3B8] text-sm mt-1">Needs attention</p>
            </div>
          </div>
        </div>

        <div>
          <h3 className="text-lg font-medium text-white mb-3">Brand CI Tiers</h3>
          <div className="grid grid-cols-3 gap-4">
            <div className="bg-[#3EF1C7]/10 border border-[#3EF1C7]/30 rounded-lg p-4 text-center">
              <div className="text-2xl font-bold text-[#3EF1C7]">70–100</div>
              <div className="text-white font-medium">Strong</div>
              <p className="text-[#94A3B8] text-sm mt-1">Growing momentum</p>
            </div>
            <div className="bg-[#94A3B8]/10 border border-[#94A3B8]/30 rounded-lg p-4 text-center">
              <div className="text-2xl font-bold text-[#94A3B8]">40–69</div>
              <div className="text-white font-medium">Stable</div>
              <p className="text-[#94A3B8] text-sm mt-1">Holding steady</p>
            </div>
            <div className="bg-[#FFD166]/10 border border-[#FFD166]/30 rounded-lg p-4 text-center">
              <div className="text-2xl font-bold text-[#FFD166]">0–39</div>
              <div className="text-white font-medium">Under Pressure</div>
              <p className="text-[#94A3B8] text-sm mt-1">Declining trend</p>
            </div>
          </div>
        </div>
      </section>

      {/* How Product CI Score Works */}
      <section className="mb-12">
        <h2 className="text-xl font-semibold text-white mb-4">
          How Product CI Score v0 Works
        </h2>
        <div className="prose prose-invert max-w-none">
          <p className="text-[#94A3B8] mb-4">
            For each product within a brand and window (e.g. last 30 days), CI Score v0:
          </p>
          <ol className="text-[#94A3B8] space-y-2 mb-4 list-decimal list-inside">
            <li>Counts views and purchases for that product</li>
            <li>Computes its conversion rate</li>
            <li>Compares that product&apos;s views and conversion to the <strong className="text-white">median product</strong> in your catalog</li>
            <li>Combines an &ldquo;activity index&rdquo; (traffic) and a &ldquo;conversion index&rdquo; (how well that traffic converts) into a single raw score</li>
            <li>Scales that raw score into the 0–100 range</li>
          </ol>
          
          <Card className="bg-[#0D1628] border-[#22304A] mb-4">
            <CardContent className="pt-4">
              <h4 className="text-white font-medium mb-2">Roughly:</h4>
              <ul className="text-[#94A3B8] text-sm space-y-1 list-disc list-inside">
                <li>A product with average views and average conversion sits around the middle of the range</li>
                <li>Products with significantly higher views and higher conversion than the brand median get higher scores</li>
                <li>Products with much lower activity or poor conversion get lower scores</li>
              </ul>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* How Brand CI Score Works */}
      <section className="mb-12">
        <h2 className="text-xl font-semibold text-white mb-4">
          How Brand CI Score v0 Works
        </h2>
        <div className="prose prose-invert max-w-none">
          <p className="text-[#94A3B8] mb-4">
            For each brand and window (e.g. last 30 days), CI Score v0:
          </p>
          <ol className="text-[#94A3B8] space-y-2 mb-4 list-decimal list-inside">
            <li>Sums all views and purchases for the brand</li>
            <li>Computes the brand&apos;s conversion rate for that window</li>
            <li>Compares views, purchases, and conversion to the <strong className="text-white">previous time window</strong> (e.g. previous 30 days)</li>
            <li>Applies a weighted uplift or penalty to a baseline score based on whether things are trending up, flat, or down</li>
            <li>Clamps the result into the 0–100 range</li>
          </ol>
          
          <Card className="bg-[#0D1628] border-[#22304A] mb-4">
            <CardContent className="pt-4">
              <h4 className="text-white font-medium mb-2">Roughly:</h4>
              <ul className="text-[#94A3B8] text-sm space-y-1 list-disc list-inside">
                <li>If your views, purchases, and conversion are all materially up vs last period, your brand CI Score goes up</li>
                <li>If they are all down, your brand CI Score goes down</li>
                <li>If they are mixed or flat, your brand CI Score stays around the middle</li>
              </ul>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* What CI Score Is and Is Not */}
      <section className="mb-12">
        <h2 className="text-xl font-semibold text-white mb-4 flex items-center gap-2">
          <AlertCircle className="w-5 h-5 text-[#FFD166]" />
          What CI Score v0 Is (and Is Not)
        </h2>
        
        <div className="grid md:grid-cols-2 gap-6">
          <Card className="bg-[#3EF1C7]/5 border-[#3EF1C7]/30">
            <CardHeader className="pb-2">
              <CardTitle className="text-[#3EF1C7] text-lg">CI Score v0 IS:</CardTitle>
            </CardHeader>
            <CardContent>
              <ul className="text-[#94A3B8] text-sm space-y-2">
                <li className="flex items-start gap-2">
                  <span className="text-[#3EF1C7] mt-0.5">✓</span>
                  A simple, relative signal built entirely from your own events
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-[#3EF1C7] mt-0.5">✓</span>
                  A way to quickly see which products and brands are hot/steady/cold without deep analysis
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-[#3EF1C7] mt-0.5">✓</span>
                  An early &ldquo;lens&rdquo; into the broader Commerce Index we are building
                </li>
              </ul>
            </CardContent>
          </Card>
          
          <Card className="bg-[#FF6B6B]/5 border-[#FF6B6B]/30">
            <CardHeader className="pb-2">
              <CardTitle className="text-[#FF6B6B] text-lg">CI Score v0 is NOT:</CardTitle>
            </CardHeader>
            <CardContent>
              <ul className="text-[#94A3B8] text-sm space-y-2">
                <li className="flex items-start gap-2">
                  <span className="text-[#FF6B6B] mt-0.5">✗</span>
                  A credit score or a judgment of your business
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-[#FF6B6B] mt-0.5">✗</span>
                  A multi-pillar macro index (no category-level demand, supply, or logistics yet)
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-[#FF6B6B] mt-0.5">✗</span>
                  A replacement for your own KPIs — it is a complement that sits alongside them
                </li>
              </ul>
            </CardContent>
          </Card>
        </div>
        
        <p className="text-[#94A3B8] mt-6 text-sm">
          As we expand data coverage and add more pillars (category demand, supply, pricing environment, logistics, etc.), 
          CI Score will evolve into a richer, more complete Commerce Index. v0 is intentionally simple and transparent 
          so you can trust what it is doing from day one.
        </p>
      </section>

      {/* API Response Examples */}
      <section className="mb-12">
        <h2 className="text-xl font-semibold text-white mb-4">
          CI Score in API Responses
        </h2>
        
        <div className="space-y-4">
          <div>
            <h3 className="text-lg font-medium text-white mb-2">Product Signals Response</h3>
            <pre className="bg-[#0D1628] border border-[#22304A] rounded-lg p-4 overflow-x-auto text-sm">
              <code className="text-[#E2E8F0]">{`{
  "signal": {
    "activity_tier": "high",
    "comment": "Strong recent activity and stable conversion.",
    "ci_score": 82,
    "ci_tier": "hot"
  }
}`}</code>
            </pre>
          </div>
          
          <div>
            <h3 className="text-lg font-medium text-white mb-2">Brand Signals Response</h3>
            <pre className="bg-[#0D1628] border border-[#22304A] rounded-lg p-4 overflow-x-auto text-sm">
              <code className="text-[#E2E8F0]">{`{
  "aggregate": {
    "total_views": 128450,
    "total_purchases": 6425,
    "overall_conversion_rate": 0.05,
    "ci_score": 57
  }
}`}</code>
            </pre>
          </div>
        </div>
      </section>

      {/* Known Limitations */}
      <section className="mb-12">
        <h2 className="text-xl font-semibold text-white mb-4 flex items-center gap-2">
          <AlertCircle className="w-5 h-5 text-[#FFD166]" />
          CI Score v0 – Known Limitations
        </h2>
        <p className="text-[#94A3B8] mb-6">
          CI Score v0 is intentionally simple and conservative. It is designed to be useful early, not perfect. 
          There are a few important limitations to be aware of:
        </p>

        <div className="space-y-6">
          {/* Limitation 1 */}
          <Card className="bg-[#0D1628] border-[#22304A]">
            <CardHeader className="pb-2">
              <CardTitle className="text-white text-base flex items-center gap-2">
                <span className="text-[#FFD166]">1.</span> It is built only on view + purchase events (for now)
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-[#94A3B8] text-sm mb-3">
                CI Score v0 does not yet incorporate:
              </p>
              <ul className="text-[#94A3B8] text-sm space-y-1 list-disc list-inside mb-3">
                <li>broader category demand</li>
                <li>pricing environment across the market</li>
                <li>logistics performance</li>
                <li>returns data</li>
              </ul>
              <p className="text-[#94A3B8] text-sm">
                It is a <strong className="text-white">local signal</strong> based on your own traffic and conversion. 
                Future versions of CI will layer in more pillars.
              </p>
            </CardContent>
          </Card>

          {/* Limitation 2 */}
          <Card className="bg-[#0D1628] border-[#22304A]">
            <CardHeader className="pb-2">
              <CardTitle className="text-white text-base flex items-center gap-2">
                <span className="text-[#FFD166]">2.</span> It is relative to your catalog and recent history
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ul className="text-[#94A3B8] text-sm space-y-1 list-disc list-inside mb-3">
                <li>Product scores compare each SKU to the median of your own products in the same window</li>
                <li>Brand scores compare your current period to your own previous period</li>
              </ul>
              <p className="text-[#94A3B8] text-sm mb-2">This means:</p>
              <ul className="text-[#94A3B8] text-sm space-y-1 list-disc list-inside">
                <li>CI Score v0 is very good at telling you which products are doing better or worse than your norm, and whether your brand trend is up, flat, or under pressure</li>
                <li>It is <strong className="text-white">not a cross-brand or cross-merchant comparison yet</strong>. A score of 80 for one brand is not directly comparable to a score of 80 for another brand</li>
              </ul>
            </CardContent>
          </Card>

          {/* Limitation 3 */}
          <Card className="bg-[#0D1628] border-[#22304A]">
            <CardHeader className="pb-2">
              <CardTitle className="text-white text-base flex items-center gap-2">
                <span className="text-[#FFD166]">3.</span> Low-volume products can have fragile scores
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-[#94A3B8] text-sm mb-3">
                For very low-traffic products (e.g. only a handful of views or purchases):
              </p>
              <ul className="text-[#94A3B8] text-sm space-y-1 list-disc list-inside mb-3">
                <li>small changes can produce big swings in conversion rate</li>
                <li>which in turn can swing CI Score v0 more than you would expect</li>
              </ul>
              <p className="text-[#94A3B8] text-sm mb-2">We try to mitigate this with:</p>
              <ul className="text-[#94A3B8] text-sm space-y-1 list-disc list-inside mb-3">
                <li>minimum thresholds before we compute a full score</li>
                <li>conservative caps on how good or how bad a single period can move a score</li>
              </ul>
              <p className="text-[#94A3B8] text-sm">
                In low-volume situations, treat CI Score v0 as a <strong className="text-white">rough indicator, not a precise instrument</strong>, 
                and always look at the raw view/purchase counts alongside the score.
              </p>
            </CardContent>
          </Card>

          {/* Limitation 4 */}
          <Card className="bg-[#0D1628] border-[#22304A]">
            <CardHeader className="pb-2">
              <CardTitle className="text-white text-base flex items-center gap-2">
                <span className="text-[#FFD166]">4.</span> Short windows reflect noise as well as signal
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-[#94A3B8] text-sm mb-3">
                The shorter the time window (1d or 7d), the more CI Score v0 will:
              </p>
              <ul className="text-[#94A3B8] text-sm space-y-1 list-disc list-inside mb-3">
                <li>reflect campaign spikes</li>
                <li>micro-seasonality</li>
                <li>one-off events</li>
              </ul>
              <p className="text-[#94A3B8] text-sm mb-2">Longer windows (30d or 90d) tend to give you:</p>
              <ul className="text-[#94A3B8] text-sm space-y-1 list-disc list-inside mb-3">
                <li>a more stable view of product/brand tiers</li>
                <li>better context for strategic decisions</li>
              </ul>
              <p className="text-[#94A3B8] text-sm">
                <strong className="text-white">Use shorter windows for tactical monitoring, and longer windows for structural decisions.</strong>
              </p>
            </CardContent>
          </Card>

          {/* Limitation 5 */}
          <Card className="bg-[#0D1628] border-[#22304A]">
            <CardHeader className="pb-2">
              <CardTitle className="text-white text-base flex items-center gap-2">
                <span className="text-[#FFD166]">5.</span> CI Score v0 will evolve
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-[#94A3B8] text-sm mb-3">
                This first version is explicitly scoped around what we can compute from your events on day one. 
                As Commerce Index expands:
              </p>
              <ul className="text-[#94A3B8] text-sm space-y-1 list-disc list-inside mb-3">
                <li>category-level demand</li>
                <li>pricing power</li>
                <li>merchant health</li>
                <li>brand momentum</li>
                <li>logistics efficiency</li>
              </ul>
              <p className="text-[#94A3B8] text-sm">
                will start to feed into CI Scores and CI proper. We will document changes as we go so you always know 
                what CI Score is measuring at each version – <strong className="text-white">v0 is meant to be a clear, humble starting point</strong>.
              </p>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* CI Score Visibility */}
      <section className="mb-8">
        <h2 className="text-xl font-semibold text-white mb-4">
          &ldquo;CI Score Visibility Included&rdquo;
        </h2>
        <Card className="bg-[#7A5CFF]/10 border-[#7A5CFF]/30">
          <CardContent className="pt-4">
            <p className="text-[#E2E8F0]">
              <strong className="text-white">CI Score visibility</strong> gives you early access to basic Commerce Index signals 
              for your brand and products — including activity tiers and basic demand/momentum indicators — 
              ahead of full category and market CI.
            </p>
            <p className="text-[#94A3B8] text-sm mt-3">
              You can see CI Score in your CI for Brands dashboard and in your Signals responses (if allowed for your plan).
            </p>
          </CardContent>
        </Card>
      </section>
    </div>
  );
}
